import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import * as moment from 'moment';
import { Duration } from 'moment';
import { Moment } from 'moment/moment';
import { DistanceUtils } from '../../shared/distance-utils';
import { DistanceUnit } from '../../shared/distance-unit';
import { Trip } from '../shared/trip.model';
import { TripPingLight } from '../shared/trip-ping-light.model';
import { TimelineEvent } from '../shared/timeline-event';
import { TimelineInfo } from '../shared/timeline-info';
import { TimelineEventType } from '../shared/timeline-event-type';
import { InfoType } from '../shared/info-type';
import {
  HandoverZone, ScaleType,
  TimelineSegment,
  TimelineValue,
  TimelineZone,
  ZoneClass
} from '../shared/timeline-segment';
import { TripEventType } from '../shared/trip-event-type';
import { LoadHandover } from '../../load-handover/shared/load-handover.model';
import { TripDelayType } from '../shared/trip-delay-type';
import { TripStatus } from '../shared/trip-status';
import { TripEvent } from '../shared/trip-event.model';
import { TripPathUtils } from '../shared/trip-path-utils';
import LatLngLiteral = google.maps.LatLngLiteral;
import { DateTimezoneService } from '../shared/date-timezone.service';
import { DateService } from '../../shared/date.service';

const NB_SCALE = 5;
const SCALE_MIN_SIZE = 7.5;
const ELLAPSED_TIME_THRESHOLD_IN_MINUTES = 30;
const DISTANCE_THRESHOLD_IN_KM_OR_MILES = 10;

@Component({
  selector: 'app-trip-timeline',
  templateUrl: './trip-timeline.component.html',
  styleUrls: ['./trip-timeline.component.scss']
})
export class TripTimelineComponent implements OnInit, OnChanges, AfterViewInit {

  @Input()
  trip: Trip;

  @Input()
  distanceUnit: DistanceUnit;

  @Input()
  refreshing: boolean;

  @Output()
  tripChange = new EventEmitter<Trip>();

  @Output()
  truckMoved = new EventEmitter<TripPingLight>();

  @Output()
  onRefresh = new EventEmitter<any>();

  @ViewChild('wrapper') timelineWrapper: ElementRef;

  info: TimelineInfo;
  etaInfos: EtaInfos;
  planningStartPos: number;
  planningEndPos: number;
  noDataSegments: Array<TimelineInfo> = [];

  cursor: number;
  cursorDate: Moment;
  cursorDateMin: Moment;
  cursorDateMax: Moment;
  vehicleName: string;
  lastVehicle: string;
  pingStart: number;
  pingEnd: number;
  tripDuration: number;

  zoomActive = false;
  zoom = 1;
  ready = false;
  showAllEtas = false;
  showLegend = false;

  legendTab = 0;
  pauseSize = 10;

  now: Moment;
  min: Moment;
  max: Moment;

  remainingSpace = 0;

  TripStatus = TripStatus;
  TimelineEventType = TimelineEventType;
  eventType: TimelineEventType;
  timelineSegments: Array<TimelineSegment> = [];
  handoverZones: Array<HandoverZone> = [];
  tripStatus = TripDelayType.UNKNOWN;
  tripAchievedPath: TripPingLight[];

  constructor(
    private cdr: ChangeDetectorRef,
    private timeZoneService: DateTimezoneService,
    private dateService: DateService) {
  }

  ngOnInit(): void {
    this.timeZoneService.timezoneChange.subscribe(() => this.refreshTimeline(true));
    // Listen for language selector changes
    this.dateService.localeChanged.subscribe(() => {
      this.updateTripDates(this.dateService.currentLanguageMomentCode);
      this.refreshTimeline(true);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.trip) {
      return;
    }
    if (changes && (changes.trip || (changes.refreshing && changes.refreshing.previousValue === true) || changes.timeZoneOffset)) {
      this.refreshTimeline(changes.trip.currentValue !==  changes.trip.previousValue, );
    }
  }

  ngAfterViewInit(): void {
    this.ready = true;
    this.setRemainingSpace();
    this.cursorChanged(true);
  }

  private refreshTimeline(tripChanged: boolean) {
    if (!this.trip) {
      return;
    }
    if (tripChanged) {
      this.min = this.timeZoneService.utcToCurrentTimezone(this.trip.getTripStart());
      this.now = this.timeZoneService.utcToCurrentTimezone(moment());
      this.max = this.timeZoneService.utcToCurrentTimezone(this.trip.getTripEnd());

      this.setEtaInfosFromTrip();
      this.initPlanningTimelineSegment();
      this.tripStatus = this.trip.getTripStatus();
      this.tripAchievedPath = TripPathUtils.getAggregatedPingsFromTripSortedByCreation(this.trip);
      if (this.trip.latestPing) {
        this.cursorDateMin = this.timeZoneService.utcToCurrentTimezone(this.tripAchievedPath[0]?.createdAt);
        this.cursorDateMax = this.timeZoneService.utcToCurrentTimezone(this.trip.latestPingCreatedAt);
        this.vehicleName = this.trip.vehicle;
      }
      this.tripDuration = this.timeZoneService.utcToCurrentTimezone(this.trip.getTripEnd())?.diff(this.timeZoneService.utcToCurrentTimezone(this.trip.getTripStart()), 'hours');
      if (this.min) {
        this.buildTimelineSegmentsFromAchievedPathPings();
        this.buildLoadHandoverZones();
        this.setRemainingSpace();
        this.cursorChanged(tripChanged);
      }
      this.cdr.detectChanges();
    }
  }

  private setEtaInfosFromTrip(): void {
    this.etaInfos = {
      from: this.trip.loadPlanningStart,
      to:  this.trip.loadPlanningEnd,
      provider: null,
      lastPing: this.trip.latestPing ? this.trip.latestPingCreatedAt : null,
      lastPingToNow: this.trip.latestPing ? this.dateService.durationFrom(this.trip.latestPingCreatedAt, moment(), false).duration : null
    }
  }

  /**
   * Determines the begining and the end of the timeline segments from the trip's available dates regarding the <b>planning</b>
   */
  private initPlanningTimelineSegment(): void {
    const loadPlanningStart = this.timeZoneService.utcToCurrentTimezone(this.trip.loadPlanningStart);
    const loadPlanningEnd = this.timeZoneService.utcToCurrentTimezone(this.trip.loadPlanningEnd);

    if (loadPlanningStart && loadPlanningEnd) {
      this.planningStartPos = this.clamp(0, loadPlanningStart.diff(this.min) * 100 / this.max.diff(this.min), 100);
      this.planningEndPos = this.clamp(
        0,
        loadPlanningEnd.diff(this.min) * 100 / this.max.diff(this.min) - loadPlanningStart.diff(this.min) * 100 / this.max.diff(this.min),
        100 - this.planningStartPos
      );
    }
  }

  /**
   *  Build segments of the timeline from each ping defined in the trip's achieved paths.
   *  The timeline segments are separated by a "no data" segment if two following positions (pings)
   *  are too far away from each other defined here: {@link isNextPositionFurtherEnoughFromCurrentPos}.
   */
  private buildTimelineSegmentsFromAchievedPathPings(): void {
    this.timelineSegments = [];

    let beginDate = this.min.clone();
    let timelineBeginPos = 0;
    if (this.tripAchievedPath.length > 0) {
      let currentPos = this.tripAchievedPath[0];
      for (const nextPos of this.tripAchievedPath) {
        if (currentPos === nextPos) {
          continue;
        }
        const distanceBetweenCurrentAndNextPos = DistanceUtils.distanceBetweenCoords(
          currentPos.latitude,
          currentPos.longitude,
          nextPos.latitude,
          nextPos.longitude,
          this.distanceUnit
        );

        if (this.isNextPositionFurtherEnoughFromCurrentPos(currentPos, nextPos, distanceBetweenCurrentAndNextPos)) {
          const timelineEndPos = currentPos.createdAt.diff(this.min) * 100 / this.max.diff(this.min);

          this.addNewTimelineSegment(beginDate, this.timeZoneService.utcToCurrentTimezone(currentPos.createdAt), timelineBeginPos, timelineEndPos);
          this.addNewNoDataSegment(currentPos.createdAt, nextPos.createdAt, timelineEndPos, distanceBetweenCurrentAndNextPos);

          beginDate = this.timeZoneService.utcToCurrentTimezone(nextPos.createdAt);
          timelineBeginPos = beginDate.diff(this.min) * 100 / this.max.diff(this.min);
        }
        currentPos = {
          ...nextPos,
          toLatLntLiteral(): LatLngLiteral {
            return {lat: this.latitude, lng: this.longitude};
          }
        };
      }
    }
    this.addNewTimelineSegment(beginDate, this.max.clone(), timelineBeginPos, 100, true);

    // Fill zones, scales and events data on each segment created
    for (const timelineSegment of this.timelineSegments) {
      this.calculateScalesOfASegment(timelineSegment);
      if (this.tripAchievedPath) {
        this.setValuesAndZonesOfTimelineSegment(timelineSegment);
      }
      this.addEventsToTimelineSegment(timelineSegment);
    }
  }

  /**
   *  Build the handover related data from the selected trip and include it in the timeline.
   *  <b>The timeline segments must first be built before.</b>
   */
  private buildLoadHandoverZones(): void {
    this.handoverZones = [];

    for (const handoverData of this.trip.loadHandovers) {
      const handover = {...handoverData};
      if (!handover.takenAt) {
        handover.takenAt = this.now.clone();
      }
      const takenAt = this.timeZoneService.utcToCurrentTimezone(handover.takenAt);
      let inSameTimeline = true;

      const releasedAt = this.timeZoneService.utcToCurrentTimezone(handover.releasedAt);
      for (const timeline of this.timelineSegments) {
        if (releasedAt.isBetween(timeline.begin, timeline.end, undefined, '[)') &&
          !takenAt.isBetween(timeline.begin, timeline.end, undefined, '[)')) {
          inSameTimeline = false;
        }
      }
      const duration = moment.duration(takenAt.diff(releasedAt));
      const posRelease = releasedAt.diff(this.min) * 100 / this.max.diff(this.min);
      const posTake = takenAt.diff(this.min) * 100 / this.max.diff(this.min);
      this.handoverZones.push({
        zone: {
          start: this.dateService.formatWithLocale(releasedAt),
          end: this.dateService.formatWithLocale(takenAt),
          pos: posRelease,
          duration: posTake - posRelease,
          class: 'handover',
          info: {
            pos: posRelease + (posTake - posRelease) / 2,
            time1: releasedAt,
            time2: takenAt,
            preferredTimeZone: this.trip.timezone,
            endDate: takenAt,
            duration: duration.days() + 'd ' + duration.hours() + 'h ' + duration.minutes() + 'm',
            handoverVehicle1: handover.releasedByVehicle,
            handoverVehicle2: handover.takenByVehicle,
            type: InfoType.HANDOVER,
            provider: null,
            active: false,
            distance: null,
            distanceTravelled: null,
            distanceRemaining: null,
            distanceUnit: null
          }
        },
        inSameTimeline: inSameTimeline
      });
    }
  }

  /**
   * Checks if there is at least more than 30 mins between the positions and at least 10 KM or Miles (the two selectable values).
   *
   * @param currentPos  current ping position
   * @param nextPos     next ping position
   * @param distanceBetweenCurrentAndNextPos    distance value (either KM or Miles)
   */
  private isNextPositionFurtherEnoughFromCurrentPos(
    currentPos: TripPingLight,
    nextPos: TripPingLight,
    distanceBetweenCurrentAndNextPos: number
  ): boolean {
    return this.timeZoneService.utcToCurrentTimezone(currentPos.createdAt).clone()
        .add(ELLAPSED_TIME_THRESHOLD_IN_MINUTES, 'minutes')
        .isBefore(this.timeZoneService.utcToCurrentTimezone(nextPos.createdAt))
      && distanceBetweenCurrentAndNextPos > DISTANCE_THRESHOLD_IN_KM_OR_MILES
  }

  /**
   * Add a new timeline to the list shown in the component from the date and indexes endpoints of the segment.
   *
   * @param beginDate   begin date of the timeline
   * @param endDate     end date of the timeline
   * @param beginPos    index position at which the timeline begins
   * @param endPos      index position at which the timeline ends
   * @param isLast      indicates if it is the last segment of the timeline
   */
  private addNewTimelineSegment(
    beginDate: Moment,
    endDate: Moment,
    beginPos: number,
    endPos: number,
    isLast: boolean = false
  ): void {
    this.timelineSegments.push({
      begin: beginDate,
      end: endDate,
      pos: beginPos,
      duration: endPos - beginPos,
      scales: [],
      values: [],
      zones: [],
      events: [],
      first: beginPos === 0,
      last: isLast
    });
  }

  /**
   * Add a new 'No Data' segment in the timeline. One is added if two following positions (pings) are too far apart.
   *
   * @param beginDate                         1st ping date which encapsulate the no data segment
   * @param endDate                           2nd ping date which encapsulate the no data segment
   * @param position                          position at which the segment will be
   * @param distanceBetweenCurrentAndNextPos  total distance which separates two timeline segments (two pings)
   * @private
   */
  private addNewNoDataSegment(
    beginDate: Moment,
    endDate: Moment,
    position: number,
    distanceBetweenCurrentAndNextPos: number
  ): void {
    const duration = moment.duration(endDate.diff(beginDate));
    this.noDataSegments.push({
      pos: position,
      time1: beginDate,
      time2: endDate,
      preferredTimeZone: this.trip.timezone,
      endDate: endDate,
      duration: duration.days() + 'd ' + duration.hours() + 'h ' + duration.minutes() + 'm',
      distance: Math.round(distanceBetweenCurrentAndNextPos),
      distanceUnit: this.distanceUnit,
      type: InfoType.NO_DATA,
      provider: null,
      active: false,
      handoverVehicle1: null,
      handoverVehicle2: null,
      distanceRemaining: null,
      distanceTravelled: null
    });
  }

  /**
   * Calculate and add the scales at which the timeline segment will be shown.
   * <b>We must have all the timelines added first before doing it.</b>
   *
   * @param segment  timeline segment on which we add the calculated scales
   */
  calculateScalesOfASegment(segment: TimelineSegment): void {
    segment.scales = [];
    const scaleDate = this.timeZoneService.utcToCurrentTimezone(segment.begin.clone());
    this.zoom = Math.ceil(this.max.diff(this.min, 'h') / NB_SCALE);
    let remainder = 0;
    const remainderUnit = this.zoomActive ? 60 : 30;
    const spacing = this.zoomActive ? NB_SCALE * this.zoom : NB_SCALE;
    this.addNewScaleToSegment(segment, segment.begin, 0, 'scale');

    let etaPosition: number;
    if (this.trip.eta() && !!this.trip.events.find((event: TripEvent) => event.referenceDate.isSame(this.trip.eta()))) {
      const tripEta = this.timeZoneService.utcToCurrentTimezone(this.trip.eta());
      etaPosition = this.timeZoneService.utcToCurrentTimezone(tripEta).diff(segment.begin) * 100 / segment.end.diff(segment.begin);
      if (Math.abs(segment.pos - etaPosition) < (this.zoomActive ? SCALE_MIN_SIZE / this.zoom : SCALE_MIN_SIZE)) {
        segment.scales.pop();
      }
      if (this.timeZoneService.utcToCurrentTimezone(tripEta).isBetween(segment.begin, segment.end, undefined, '[]')) {
        this.addNewScaleToSegment(segment, tripEta, etaPosition, this.determineSelectedTripScaleType());
      }
    }
    for (let i = 0; i <= spacing; i++) {
      remainder = remainderUnit - (scaleDate.add(this.max.diff(this.min) / spacing).minutes()) % remainderUnit;
      scaleDate.add(remainder, 'm');

      const timelineIndex = this.timelineSegments.indexOf(segment);
      if (!scaleDate.isBetween(segment.begin, segment.end) ||
        (this.zoomActive && !segment.last && this.timelineSegments[timelineIndex + 1].begin.diff(this.min) * 100 / this.max.diff(this.min) -
          scaleDate.diff(this.min) * 100 / this.max.diff(this.min) < SCALE_MIN_SIZE) ||
        (!this.zoomActive && segment.end.diff(this.min) * 100 / this.max.diff(this.min) -
          scaleDate.diff(this.min) * 100 / this.max.diff(this.min) < SCALE_MIN_SIZE)
      ) {
        break;
      }
      const scalePosition = scaleDate.diff(segment.begin) * 100 / segment.end.diff(segment.begin);
      this.addNewScaleToSegment(segment, scaleDate, scalePosition, 'scale');

      if (this.trip.eta() && Math.abs(scalePosition - etaPosition) <= (this.zoomActive ? SCALE_MIN_SIZE / this.zoom : SCALE_MIN_SIZE)) {
        segment.scales.pop();
      }
    }

    if (this.ready && this.zoomActive) {
      // To wait till the width of the timeline is fully set
      setTimeout(() => {
        // bar.style.scrollBehavior = 'smooth';
        this.resetScrollPositionOfTimeline();
        // bar.style.scrollBehavior = 'auto';
      }, 500);
    }
  }

  private resetScrollPositionOfTimeline(): void {
    const bar = document.getElementById('bar-wrapper');
    bar.scrollLeft = Math.round((bar.scrollWidth * this.cursor / 100) - (bar.scrollWidth / 2 / this.zoom));
  }

  private addNewScaleToSegment(
    segment: TimelineSegment,
    date: Moment,
    position: number,
    type: ScaleType
  ): void {
    segment.scales.push({
      date: this.timeZoneService.utcToCurrentTimezone(date),
      pos: position,
      type: type
    });
  }

  private determineSelectedTripScaleType(): ScaleType {
    return ((this.trip.status === TripStatus.IN_PROGRESS && this.trip.eta().isBefore(this.now))
      || ((this.trip.status === TripStatus.COMPLETED || this.trip.status === TripStatus.EXPIRED) && this.trip.eta().isBefore(this.trip.completedAt)))
      ? 'red' : 'green';
  }

  setValuesAndZonesOfTimelineSegment(segment: TimelineSegment): void {
    const firstPing = this.tripAchievedPath[0];
    if (segment.first && firstPing && !this.trip.isPlanned()) {
      const createdAt = this.timeZoneService.utcToCurrentTimezone(firstPing.createdAt);
      const firstPingPosition = this.clamp(0, createdAt.diff(segment.begin) * 100 / segment.end.diff(segment.begin), 100);
      const duration = moment.duration(createdAt.diff(segment.end));
      segment.values.push({
        x: {
          time: this.dateService.formatWithLocale(segment.begin, 'LT'),
          pos: 0
        },
        y: {
          time: this.dateService.formatWithLocale(createdAt, 'LT'),
          pos: firstPingPosition
        },
        pingX: firstPing,
        pingY: firstPing,
        class: 'standby'
      });
      segment.zones.push({
        start: this.dateService.formatWithLocale(segment.end, 'LT'),
        end: this.dateService.formatWithLocale(createdAt, 'LT'),
        pos: 0,
        duration: firstPingPosition,
        class: 'standby',
        info: {
          pos: firstPingPosition / 2,
          time1: segment.end,
          time2: createdAt,
          preferredTimeZone: this.trip.timezone,
          endDate: segment.end,
          duration: (duration.days() ? duration.days() + 'd ' : '') +
            duration.hours() + 'h ' + duration.minutes() + 'm',
          type: InfoType.STANDBY,
          active: false,
          distance: null, distanceTravelled: null, distanceRemaining: null, distanceUnit: null,
          handoverVehicle1: null, handoverVehicle2: null, provider: null,
        }
      });
    }

    this.fillValuesOfSegment(segment);
    this.fillZonesOfSegment(segment);
  }

  fillValuesOfSegment(segment: TimelineSegment): void {
    let pingX = this.tripAchievedPath[0];
    for (const pingY of this.tripAchievedPath) {
      const xCreatedAt = this.timeZoneService.utcToCurrentTimezone(pingX.createdAt);
      const yCreatedAt = this.timeZoneService.utcToCurrentTimezone(pingY.createdAt);
      if (yCreatedAt.isBetween(segment.begin, segment.end, undefined, '[)') &&
        segment.values.length === 0 && pingY === this.trip.latestPing) {
        segment.values.push({
          x: {
            time: this.dateService.formatWithLocale(moment.max(xCreatedAt, segment.begin), 'LT'),
            pos: this.clamp(0, xCreatedAt.diff(segment.begin) * 100 / segment.end.diff(segment.begin), 100)
          },
          y: {
            time: this.dateService.formatWithLocale(yCreatedAt, 'LT'),
            pos: this.clamp(0, yCreatedAt.diff(segment.begin) * 100 / segment.end.diff(segment.begin), 100)
          },
          pingX: pingX,
          pingY: pingY,
          class: 'moving'
        });
      }
      if (pingX !== pingY && xCreatedAt.isBetween(segment.begin, segment.end, undefined, '[)')) {
        const distance = DistanceUtils.distanceBetweenCoords(
          pingX.latitude, pingX.longitude, pingY.latitude, pingY.longitude, this.distanceUnit);
        segment.values.push({
          x: {
            time: this.dateService.formatWithLocale(xCreatedAt, 'LT'),
            pos: this.clamp(0, xCreatedAt.diff(segment.begin) * 100 / segment.end.diff(segment.begin), 100)
          },
          y: {
            time: this.dateService.formatWithLocale(moment.min(yCreatedAt, segment.end), 'LT'),
            pos: this.clamp(0, yCreatedAt.diff(segment.begin) * 100 / segment.end.diff(segment.begin), 100)
          },
          pingX: pingX,
          pingY: pingY,
          class: xCreatedAt.clone().add(this.pauseSize, 'minutes').isBefore(yCreatedAt) &&
          distance < 1 ?
            'stop' :
            'moving'
        });
      }
      pingX = {...pingY,
        toLatLntLiteral(): LatLngLiteral {
          return { lat: this.latitude, lng: this.longitude };
        }
      };
    }
  }

  fillZonesOfSegment(timeline: TimelineSegment): void {
    let currentZone: ZoneClass = 'moving';
    let startZone = {...timeline.values[0]};
    if (timeline.first) {
      startZone.x = {...startZone.y};
    }
    let endZone;
    for (const val of timeline.values) {
      if ((val.class !== currentZone && val.class !== 'eta' && val.class !== 'standby') ||
        (val === timeline.values[timeline.values.length - 1])) {
        endZone = {...val};
        const last = val === timeline.values[timeline.values.length - 1];
        if (last && val.class === currentZone) {
          this.addZoneToSegment(timeline, currentZone, startZone, endZone, last);
        } else if (last) {
          this.addZoneToSegment(timeline, currentZone, startZone, endZone, !last);
          this.addZoneToSegment(timeline, val.class, endZone, endZone, last);
        } else {
          this.addZoneToSegment(timeline, currentZone, startZone, endZone, last);
        }
        startZone = {...endZone};
        currentZone = val.class;
      }
    }

    let duration: Duration;
    if (timeline.values.length === 0) {
      const isCompletedOrExpired = this.trip.isCompleted() || this.trip.isExpired();
      const zoneStart = timeline.begin;
      const zoneEnd = isCompletedOrExpired
        ? this.trip.completedAt
        : this.trip.isPlanned()
          ? this.trip.loadPlanningEnd
          : this.now;
      duration = moment.duration(zoneEnd.diff(zoneStart));
      const totalDistance = DistanceUtils.distanceBetweenCoords(
          this.trip.originPosition.lat, this.trip.originPosition.lng,
          this.trip.destPosition.lat, this.trip.destPosition.lng,
          this.distanceUnit
      );
      timeline.zones.push({
        start: this.dateService.formatWithLocale(zoneStart, 'LT'),
        end: this.dateService.formatWithLocale(zoneEnd, 'LT'),
        pos: zoneStart.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin),
        duration: (zoneEnd.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin)) -
          (zoneStart.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin)),
        class: isCompletedOrExpired ? 'no-data-completed' : 'waiting',
        info: {
          pos: zoneStart.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin) +
            ((zoneEnd.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin)) -
              (zoneStart.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin))) / 2,
          time1: zoneStart,
          time2: zoneEnd,
          preferredTimeZone: this.trip.timezone,
          endDate: zoneEnd,
          duration: duration.days() + 'd ' + duration.hours() + 'h ' + duration.minutes() + 'm',
          distanceTravelled: 0,
          distanceRemaining: 0,
          distanceUnit: this.distanceUnit,
          type: isCompletedOrExpired ? InfoType.NO_DATA : InfoType.WAITING,
          active: false,
          distance: isCompletedOrExpired ? totalDistance : null,
          provider: null,
          handoverVehicle1: null,
          handoverVehicle2: null
        }
      })
    }

    if (this.trip.isInProgress() && timeline.last) {
      const distance = this.trip.latestPing ? Math.round(DistanceUtils.distanceBetweenCoords(
        this.trip.originPosition.lat, this.trip.originPosition.lng,
        this.trip.latestPing.latitude, this.trip.latestPing.longitude,
        this.distanceUnit)) : 0;
      const remainingDistance = this.trip.latestPing ? Math.round(DistanceUtils.distanceBetweenCoords(
        this.trip.destPosition.lat, this.trip.destPosition.lng,
        this.trip.latestPing.latitude, this.trip.latestPing.longitude,
        this.distanceUnit)) : 0;
      const zoneStart = this.trip.latestPingCreatedAt ? this.trip.latestPingCreatedAt : this.trip.getTripEnd(true);
      duration = moment.duration(this.now.diff(zoneStart));
      const endDate = this.now;
      timeline.zones.push({
        start: this.dateService.formatWithLocale(zoneStart, 'LT'),
        end: this.dateService.formatWithLocale(this.now, 'LT'),
        pos: zoneStart.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin),
        duration: (this.now.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin)) -
          (zoneStart.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin)),
        class: 'waiting',
        info: {
          pos: zoneStart.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin) +
            ((this.now.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin)) -
              (zoneStart.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin))) / 2,
          time1: zoneStart,
          time2: endDate,
          preferredTimeZone: this.trip.timezone,
          endDate: endDate,
          duration: duration.days() + 'd ' + duration.hours() + 'h ' + duration.minutes() + 'm',
          distanceTravelled: distance, distanceRemaining: remainingDistance, distanceUnit: this.distanceUnit,
          type: InfoType.WAITING,
          active: false,
          distance: null, provider: null,
          handoverVehicle1: null, handoverVehicle2: null
        }
      });
      duration = moment.duration(this.max.diff(this.now));
      timeline.zones.push({
        start: this.dateService.formatWithLocale(this.now, 'LT'),
        end: this.dateService.formatWithLocale(timeline.end, 'LT'),
        pos: this.now.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin),
        duration: 100 - this.now.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin),
        class: 'remain',
        info: {
          pos: this.now.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin) +
            (100 - this.now.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin)) / 2,
          time1: this.trip.loadPlanningStart ? this.timeZoneService.utcToCurrentTimezone(this.trip.loadPlanningStart) : null,
          time2: this.trip.loadPlanningEnd ? this.timeZoneService.utcToCurrentTimezone(this.trip.loadPlanningEnd) : null,
          preferredTimeZone: this.trip.timezone,
          endDate: this.timeZoneService.utcToCurrentTimezone(this.trip.loadPlanningEnd) ,
          provider: null,
          duration: duration.days() + 'd ' + duration.hours() + 'h ' + duration.minutes() + 'm',
          type: InfoType.REMAIN,
          active: false,
          distance: null, distanceTravelled: null, distanceRemaining: null, distanceUnit: null,
          handoverVehicle1: null, handoverVehicle2: null
        }
      });
    }
    const checkinOrCheckout = this.trip.events
      .filter(e => (e.type === TripEventType.CHECKIN || e.type === TripEventType.CHECKOUT) &&
        e.referenceDate.isSameOrAfter(this.trip.latestPingCreatedAt)
      )[0];
    if ((this.trip.isCompleted() || this.trip.isExpired()) && timeline.last) {
      const defaultStart = timeline.zones.length > 0 && timeline.zones[timeline.zones.length - 1].info ? timeline.zones[timeline.zones.length - 1].info.endDate : this.trip.getTripStart();
      const zoneStart = this.trip.latestPingCreatedAt ? this.timeZoneService.utcToCurrentTimezone(this.trip.latestPingCreatedAt) : defaultStart;
      const zoneEnd = this.timeZoneService.utcToCurrentTimezone(checkinOrCheckout ? checkinOrCheckout.referenceDate : this.trip.getTripEnd());
      if (this.trip.achievedPaths.length > 0 && zoneStart.isBefore(zoneEnd)) {
        duration = moment.duration(zoneEnd.diff(this.trip.latestPingCreatedAt));
        const latestPing = this.trip.latestPing ? this.trip.latestPing.toLatLntLiteral() : this.trip.originPosition;
        const distance = DistanceUtils.distanceBetweenCoords(
          latestPing.lat, latestPing.lng,
          this.trip.destPosition.lat, this.trip.destPosition.lng,
          this.distanceUnit);
        timeline.zones.push({
          start: this.dateService.formatWithLocale(zoneStart, 'LT'),
          end: this.dateService.formatWithLocale(zoneEnd, 'LT'),
          pos: zoneStart.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin),
          duration: (zoneEnd.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin)) -
            (zoneStart.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin)),
          class: checkinOrCheckout ? 'close' : 'no-data-completed',
          info: {
            pos: (zoneStart.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin)) +
              (zoneEnd.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin) -
                zoneStart.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin)) / 2,
            time1: zoneStart,
            time2: zoneEnd,
            preferredTimeZone: this.trip.timezone,
            endDate: zoneEnd,
            duration: duration.days() + 'd ' + duration.hours() + 'h ' + duration.minutes() + 'm',
            provider: null,
            type: checkinOrCheckout ? InfoType.END : InfoType.NO_DATA,
            active: false,
            distance: checkinOrCheckout ? null : distance,
            distanceTravelled: null, distanceRemaining: null, distanceUnit: null,
            handoverVehicle1: null, handoverVehicle2: null
          }
        });
      }
      duration = moment.duration(this.trip.completedAt.diff(this.trip.recordedAt));
      if (checkinOrCheckout) {
        timeline.zones.push({
          start: this.dateService.formatWithLocale(timeline.begin, 'LT'),
          end: this.dateService.formatWithLocale(this.timeZoneService.utcToCurrentTimezone(this.trip.completedAt), 'LT'),
          pos: zoneEnd.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin),
          duration: 100 - zoneEnd.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin),
          class: 'end',
          info: {
            pos: (zoneEnd.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin)) +
              (100 - zoneEnd.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin)) / 2,
            time2: this.timeZoneService.utcToCurrentTimezone(this.trip.completedAt),
            time1: this.timeZoneService.utcToCurrentTimezone(this.trip.recordedAt),
            preferredTimeZone: this.trip.timezone,
            endDate: this.timeZoneService.utcToCurrentTimezone(this.trip.completedAt),
            duration: duration.days() + 'd ' + duration.hours() + 'h ' + duration.minutes() + 'm',
            provider: null,
            type: InfoType.END_TRIP,
            active: false,
            distance: null, distanceTravelled: null, distanceRemaining: null, distanceUnit: null,
            handoverVehicle1: null, handoverVehicle2: null
          }
        });
      }
    }
  }

  /**
   * Add a zone to a segment
   *
   * @param timeline
   * @param zone
   * @param start
   * @param end
   * @param last
   */
  addZoneToSegment(timeline: TimelineSegment, zone: ZoneClass, start: TimelineValue, end: TimelineValue, last: boolean): void {
    const endPing = last ? end.pingY : end.pingX;
    const endPingCreatedAt = this.timeZoneService.utcToCurrentTimezone(endPing.createdAt);
    const duration = moment.duration(endPingCreatedAt.diff(this.timeZoneService.utcToCurrentTimezone(start.pingX.createdAt)));
    const info: TimelineInfo = {
      pos: start.x.pos + (Math.ceil(
          (endPingCreatedAt.diff(timeline.begin) * 100 / timeline.end.diff(timeline.begin) -
              start.x.pos) * 100) / 100) / 2,
      time1: this.timeZoneService.utcToCurrentTimezone(start.pingX.createdAt),
      time2: endPingCreatedAt,
      preferredTimeZone: this.trip.timezone,
      endDate: endPingCreatedAt,
      duration: duration.days() + 'd ' + duration.hours() + 'h ' + duration.minutes() + 'm',
      type: null,
      active: false,
      distance: null,
      distanceTravelled: null,
      distanceRemaining: null,
      distanceUnit: null,
      handoverVehicle1: null,
      handoverVehicle2: null,
      provider: null
    }
    switch (zone) {
      case 'moving':
        info.type = InfoType.MOVING;
        info.distance = Math.ceil(DistanceUtils.distanceBetweenCoords(
            start.pingX.latitude, start.pingX.longitude,
            endPing.latitude, endPing.longitude,
            this.distanceUnit));
          info.distanceUnit = this.distanceUnit;
        break;
      case 'stop':
        info.type = InfoType.STANDSTILL;
        break;
      case 'eta':
          info.type = InfoType.ETA;
        break;
      case 'standby':
        info.type = InfoType.STANDBY;
        break;
      default:
        console.error('Zone type not handled');
        return;
    }
    timeline.zones.push({
      start: start.x.time,
      end: end.x.time,
      pos: start.x.pos,
      duration: (last ? end.y.pos : end.x.pos) - start.x.pos,
      class: zone,
      info: info
    });
  }

  /**
   * Add events to the timeline using the select trip data.
   *
   * @param timelineSegment  timeline on which we add the events
   */
  addEventsToTimelineSegment(timelineSegment: TimelineSegment): void {
    for (const event of this.trip.events) {
      if (this.timeZoneService.utcToCurrentTimezone(event.referenceDate).isBetween(timelineSegment.begin, timelineSegment.end, undefined, '[]')) {
        let time1: Moment = this.timeZoneService.utcToCurrentTimezone(event.referenceDate);
        let time2: Moment = this.timeZoneService.utcToCurrentTimezone(event.happenedAt);
        let eventType: TimelineEventType;
        let infoType: InfoType;
        let vehicle1 = null;
        let vehicle2 = null;
        let provider = null;
        let duration = null;
        let handoverData: LoadHandover;
        switch (event.type) {
          case TripEventType.CHECKIN:
            eventType = TimelineEventType.CHECKIN;
            infoType = InfoType.CHECKIN;
            break;
          case TripEventType.CHECKOUT:
            eventType = TimelineEventType.CHECKOUT;
            infoType = InfoType.CHECKOUT;
            break;
          case TripEventType.HANDOVER_RELEASE:
            eventType = TimelineEventType.HANDOVER_RELEASE;
            infoType = InfoType.HANDOVER_RELEASE;
            handoverData = this.trip.loadHandovers.filter(handover => handover.id === event.referenceId)[0];
            if (handoverData) {
              vehicle1 = handoverData.releasedByVehicle;
            }
            break;
          case TripEventType.HANDOVER_TAKE:
            eventType = TimelineEventType.HANDOVER_TAKE;
            infoType = InfoType.HANDOVER_TAKE;
            handoverData = this.trip.loadHandovers.filter(handover => handover.id === event.referenceId)[0];
            const handoverTakenAt = this.timeZoneService.utcToCurrentTimezone(handoverData.takenAt);
            time2 = handoverTakenAt;
            if (handoverData) {
              vehicle2 = handoverData.takenByVehicle;
              if (handoverTakenAt.diff(timelineSegment.begin) * 100 / timelineSegment.end.diff(timelineSegment.begin) -
                handoverData.releasedAt.diff(timelineSegment.begin) * 100 / timelineSegment.end.diff(timelineSegment.begin) < 0.5) {
                time1 = this.timeZoneService.utcToCurrentTimezone(handoverData.releasedAt);
                eventType = TimelineEventType.HANDOVER_COMPLETE;
                infoType = InfoType.HANDOVER;
                vehicle1 = handoverData.releasedByVehicle;
                vehicle2 = handoverData.takenByVehicle;
                timelineSegment.events = timelineSegment.events.filter(timelineEvent =>
                  timelineEvent.type !== TimelineEventType.HANDOVER_RELEASE ||
                  event.referenceId !== handoverData.id);
                const durationEvent = moment.duration(handoverTakenAt.diff(this.timeZoneService.utcToCurrentTimezone(handoverData.releasedAt)));
                duration = durationEvent.days() + 'd ' + durationEvent.hours() + 'h ' + durationEvent.minutes() + 'm';
              }
            }
            break;
          case TripEventType.ETA:
            const eta = this.trip.etas.filter(etaReport => etaReport.id === event.referenceId)[0];
            if (eta) {
              if ((this.trip.status === TripStatus.IN_PROGRESS && this.timeZoneService.utcToCurrentTimezone(event.referenceDate).isBefore(this.now)) ||
                ((this.trip.status === TripStatus.COMPLETED || this.trip.status === TripStatus.EXPIRED) && event.referenceDate.isBefore(this.trip.completedAt))) {
                eventType = TimelineEventType.PRIMARY_ETA_LATE;
                infoType = InfoType.ETA_LATE;
              } else {
                eventType = TimelineEventType.PRIMARY_ETA_ON_TIME;
                infoType = InfoType.ETA_ON_TIME;
              }
              provider = eta.emitter;
              if (!event.referenceDate.isSame(this.trip.eta(), 'm')) {
                eventType = TimelineEventType.ETA;
              }
            }
            break;
        }
        timelineSegment.events.push({
          pos: time1.diff(timelineSegment.begin) * 100 / timelineSegment.end.diff(timelineSegment.begin),
          time: this.dateService.formatWithLocale(time1, 'dddd, MMMM D [at] LT'),
          type: eventType,
          info: {
            pos: time1.diff(timelineSegment.begin) * 100 / timelineSegment.end.diff(timelineSegment.begin),
            time1: time1,
            time2: time2,
            preferredTimeZone: this.trip.timezone,
            endDate: time2,
            type: infoType,
            active: false,
            handoverVehicle1: vehicle1,
            handoverVehicle2: vehicle2,
            duration: duration,
            provider: provider,
            distanceRemaining: null,
            distanceTravelled: null,
            distanceUnit: null,
            distance: null
          }
        });
      }
    }
  }

  /**
   * Handle the leftover space after building each segments, taking into account of the zoom is active or not.
   */
  setRemainingSpace(): void {
    const cursorDate = this.cursorDateMin;
    let totalWidth = 0;
    this.timelineSegments.forEach(timeline => totalWidth += timeline.duration);
    totalWidth += (this.timelineSegments.length - 1) * 2;

    const remainingWidth = 100 - totalWidth;
    this.remainingSpace = remainingWidth / this.timelineSegments.length;
    for (let i = 0; i < this.timelineSegments.length; i++) {
      let timelinePos = 0;
      for (let j = 0; j < i; j++) {
        timelinePos += this.timelineSegments[j].duration + this.remainingSpace + 2;
      }
      if (this.isFirstPositionOfTheAchievedPathInASegmentOfIndex(i)) {
        if (!this.zoomActive) {
          this.pingStart = timelinePos +
            (this.tripAchievedPath[0]?.createdAt.diff(this.timelineSegments[i].begin) * 100 /
              this.timelineSegments[i].end.diff(this.timelineSegments[i].begin) * (this.timelineSegments[i].duration + this.remainingSpace) / 100);
        } else {
          this.pingStart = this.timelineSegments[i].pos +
            this.tripAchievedPath[0]?.createdAt.diff(this.timelineSegments[i].begin) * 100 /
            this.timelineSegments[i].end.diff(this.timelineSegments[i].begin) * (this.timelineSegments[i].duration / 100);
        }
      }
      if (this.isLastPositionOfTheAchievedPathInASegmentOfIndex(i)) {
        if (!this.zoomActive) {
          this.pingEnd = timelinePos +
            (this.tripAchievedPath[this.tripAchievedPath.length - 1].createdAt.diff(this.timelineSegments[i].begin) * 100 / this.timelineSegments[i].end.diff(this.timelineSegments[i].begin) *
              (this.timelineSegments[i].duration + this.remainingSpace) / 100);
        } else {
          this.pingEnd = this.timelineSegments[i].pos +
            this.tripAchievedPath[this.tripAchievedPath.length - 1].createdAt.diff(this.timelineSegments[i].begin) * 100 / this.timelineSegments[i].end.diff(this.timelineSegments[i].begin) *
            (this.timelineSegments[i].duration / 100);
        }
      } else if (this.pingStart) {
        this.pingEnd = this.timelineSegments[i].pos + this.timelineSegments[i].duration;
      }
      if (!this.cursor) {
        this.cursor = this.pingStart;
      } else {
        if (!this.zoomActive) {
          if (cursorDate && cursorDate.isBetween(this.timelineSegments[i].begin, this.timelineSegments[i].end, undefined, '[]')) {
            this.cursor = timelinePos + cursorDate.diff(this.timelineSegments[i].begin) * 100 /
              this.timelineSegments[i].end.diff(this.timelineSegments[i].begin) * (this.timelineSegments[i].duration + this.remainingSpace) / 100;
          }
        } else {
          this.cursor = cursorDate.diff(this.min) * 100 / this.max.diff(this.min);
        }
      }
    }
  }

  private isFirstPositionOfTheAchievedPathInASegmentOfIndex(segmentIndex: number): boolean {
    return this.isPositionOfTheAchievedPathInASegmentOfIndex(0, segmentIndex);
  }

  private isLastPositionOfTheAchievedPathInASegmentOfIndex(segmentIndex: number): boolean {
    return this.isPositionOfTheAchievedPathInASegmentOfIndex(this.tripAchievedPath.length - 1, segmentIndex);
  }

  private isPositionOfTheAchievedPathInASegmentOfIndex(achievedPathIndex: number, segmentIndex: number): boolean {
    return this.tripAchievedPath[achievedPathIndex]?.createdAt.isBetween(
      this.timelineSegments[segmentIndex].begin,
      this.timelineSegments[segmentIndex].end,
      undefined,
      '[]'
    );
  }

  /**
   * - Will send the new truck's cursor position to the other components (for ex, the Map)
   * - Will update the vehicle infos
   * - Will reset the scroll of the timeline
   * - Will update the truck's cursor position and values on the timeline
   *
   * @param tripChanged
   */
  cursorChanged(tripChanged = false): void {
    if (this.trip.latestPing == null) {
      if (tripChanged && this.trip.isCompleted()) {
        // We need to reset the truck's cursor when tripAchievedPath is empty or else it can keep the previous value
        this.truckMoveEmitForCompletedTrip();
      } else if (this.tripAchievedPath.length > 0) {
        this.truckMovedEmit(this.tripAchievedPath[0], tripChanged);
      }
    }
    if (this.ready) {
      this.resetScrollPositionOfTimeline();
    }
    this.updateVehicleNameAfterCursorChanged();
    this.updateCursorPositionAndDateValues(tripChanged);
  }

  /**
   * Update the vehicle and last vehicle based on either:
   * - The trip's data
   * - The potential presence of handover
   */
  private updateVehicleNameAfterCursorChanged(): void {
    this.vehicleName = this.trip.vehicle;
    this.lastVehicle = this.trip.vehicle;

    for (const handover of this.handoverZones) {
      this.lastVehicle = handover.zone.info.handoverVehicle2 ? handover.zone.info.handoverVehicle2 : handover.zone.info.handoverVehicle1;
      if (this.cursor >= moment(handover.zone.start).diff(this.min) * 100 / this.max.diff(this.min)) {
        this.vehicleName = 'No associated vehicle';
      }
      if (this.cursor >= moment(handover.zone.end).diff(this.min) * 100 / this.max.diff(this.min)) {
        this.vehicleName = handover.zone.info.handoverVehicle2;
      }
    }
  }

  // TODO: Add description of exactly what it does later
  private updateCursorPositionAndDateValues(tripChanged: boolean): void {
    let timelinePos: number;

    for (let i = 0; i < this.timelineSegments.length; i++) {
      if (!this.zoomActive) {
        timelinePos = 0;
        for (let j = 0; j < i; j++) {
          timelinePos += this.timelineSegments[j].pos + this.timelineSegments[j].duration + this.remainingSpace + 2;
        }
        if (this.cursor >= timelinePos &&
          this.cursor <= timelinePos + this.timelineSegments[i].duration + this.remainingSpace) {
          this.cursorDate = this.timelineSegments[i].begin.clone()
            .add((this.cursor - timelinePos) / (this.timelineSegments[i].duration + this.remainingSpace) *
              this.timelineSegments[i].end.diff(this.timelineSegments[i].begin, 'ms'), 'ms');
        }
      } else {
        timelinePos = this.timelineSegments[i].pos;
        this.cursorDate = this.min.clone()
          .add(this.cursor * this.max.diff(this.min) / 100);
      }
      if (this.cursor >= timelinePos &&
        this.cursor <= timelinePos + this.timelineSegments[i].duration + (this.zoomActive ? 0 : this.remainingSpace)) {
        const valPos = (this.cursor - timelinePos) / (this.timelineSegments[i].duration + this.remainingSpace) * 100
        for (const val of this.timelineSegments[i].values) {
          if (valPos < val.x.pos) {
            this.truckMovedEmit(val.pingX, tripChanged);
            break;
          }
          if (valPos >= val.y.pos && this.trip.latestPingCreatedAt && val.y.time === this.dateService.formatWithLocale(this.trip.latestPingCreatedAt, 'LT')) {
            this.truckMovedEmit(this.trip.latestPing, tripChanged);
            break;
          }
          if (valPos >= val.x.pos &&
            valPos <= val.y.pos && val.class !== 'eta') {
            this.truckMovedEmit(val.pingY, tripChanged);
            break;
          }
        }
      }
    }
  }

  /**
   * Send the new location of the ping. If the new ping position is due to a new trip being selected while the trip is completed,
   * then send the truck cursor position being at the drop location by default.
   *
   * @param ping        last ping position to update the truck cursor
   * @param tripChanged is this change due to a new selection of a trip
   */
  private truckMovedEmit(ping: TripPingLight, tripChanged: boolean): void {
    if (tripChanged && this.trip.isCompleted()) {
      this.truckMoveEmitForCompletedTrip();
    } else {
      this.truckMoved.emit(ping);
    }
  }

  /**
   * Send the drop location position to update the truck's cursor
   */
  private truckMoveEmitForCompletedTrip(): void {
    const dropLocationPing = new TripPingLight();
    dropLocationPing.longitude = this.trip.destPosition.lng;
    dropLocationPing.latitude = this.trip.destPosition.lat;
    this.truckMoved.emit(dropLocationPing);
  }

  updateCursorDate(event: string): void {
    this.cursorDate = moment(event);
    this.cursor = Math.min(
      Math.max(moment(this.cursorDate).diff(this.min) * 100 / this.max.diff(this.min), 0),
      this.timeZoneService.utcToCurrentTimezone(this.trip.latestPingCreatedAt).diff(this.min) * 100 / this.max.diff(this.min)
    );
    this.cursorChanged();
  }

  switchZoom(): void {
    this.zoomActive = !this.zoomActive;
    for (const timeline of this.timelineSegments) {
      this.calculateScalesOfASegment(timeline);
    }
    this.setRemainingSpace();
    this.cursorChanged();
  }

  /**
   * Set current contextual info showed on screen.
   *
   * @param timelineElement set field active to true on this element.
   */
  setCurrentActiveContextualInfo(timelineElement: any): void {
    for (const timeline of this.timelineSegments) {
      for (const zone of timeline.zones) {
        zone.info.active = zone === timelineElement;
      }
      for (const event of timeline.events) {
        event.info.active = event === timelineElement;
      }
      for (const noData of this.noDataSegments) {
        noData.active = noData === timelineElement;
      }
    }
    for (const handover of this.handoverZones) {
      handover.zone.info.active = handover === timelineElement;
    }
  }

  /**
   * Set active on selected timeline element and then position the contextual info on the screen.
   *
   * @param element
   * @param i
   */
  showContextualInfoOfTimelineElement(element: TimelineZone | TimelineEvent, i: number): void {
    this.setCurrentActiveContextualInfo(element);
    this.info = {...element.info};
    if (element['type']) {
      this.eventType = element['type']
    }
    let timelinePos = 0;
    if (this.zoomActive) {
      this.info.pos = this.timelineSegments[i].pos + this.info.pos * this.timelineSegments[i].duration / 100;
    } else {
      for (let j = 0; j < i; j++) {
        timelinePos += this.timelineSegments[j].duration + this.remainingSpace + 2;
      }
      this.info.pos = timelinePos + this.info.pos * (this.timelineSegments[i].duration + this.remainingSpace) / 100;
    }
  }

  showContextualInfoNoDataSegment(noDataSegmentIndex: number): void {
    this.setCurrentActiveContextualInfo(this.noDataSegments[noDataSegmentIndex]);
    this.noDataSegments[noDataSegmentIndex].pos = this.getNoDataInfoPosition(noDataSegmentIndex);
    this.info = this.noDataSegments[noDataSegmentIndex];
  }

  showContextualInfoOfHandover(element: any): void {
    this.setCurrentActiveContextualInfo(element);
    this.info = {...element.zone.info};
  }

  clearInfo(): void {
    this.setCurrentActiveContextualInfo(null);
    this.info = null;
  }

  /**
   * Get the positions of the timeline element using its index.
   *
   * @param i index of the timeline element
   */
  getNoDataInfoPosition(i: number): number {
    let timelinePos = this.timelineSegments[i].pos + this.timelineSegments[i].duration;
    if (this.zoomActive) {
      return timelinePos + (this.timelineSegments[i + 1].pos - timelinePos) / 2;
    } else {
      timelinePos = 0;
      for (let j = 0; j < i + 1; j++) {
        timelinePos += this.timelineSegments[j].duration + this.remainingSpace + (j > 0 ? 2 : 0);
      }
      return timelinePos + 1;
    }
  }

  getNoDataZonePosition(i: number): number {
    if (this.zoomActive) {
      return this.timelineSegments[i].pos + this.timelineSegments[i].duration;
    } else {
      let timelinePos = 0;
      for (let j = 0; j < i + 1; j++) {
        timelinePos += this.timelineSegments[j].duration + this.remainingSpace + (j > 0 ? 2 : 0);
      }
      return timelinePos;
    }
  }

  refreshStatus(): void {
    this.onRefresh.emit();
  }

  /**
   * Take the bigger value between:
   * - the @min value passed
   * and
   * - the lesser value between @max and @val
   *
   * @param min
   * @param val
   * @param max
   */
  clamp(min: number, val: number, max: number): number {
    return Math.max(Math.min(val, max), min);
  }

  private updateTripDates(locale: string = 'en') {
    if (this.trip) {
      this.trip.loadPlanningStart = this.trip.loadPlanningStart?.locale(locale).clone();
      this.trip.loadPlanningEnd = this.trip.loadPlanningEnd?.locale(locale).clone();
      this.trip.checkOutDateTime = this.trip.checkOutDateTime?.locale(locale).clone();
      this.trip.checkInDateTime = this.trip.checkInDateTime?.locale(locale).clone();
      this.trip.startedAt = this.trip.startedAt?.locale(locale).clone();
      this.trip.completedAt = this.trip.completedAt?.locale(locale).clone();
      this.trip.recordedAt = this.trip.recordedAt?.locale(locale).clone();
      this.trip.latestPingCreatedAt = this.trip.latestPingCreatedAt?.locale(locale).clone();

      this.trip.events.forEach(event => {
        event.referenceDate = event.referenceDate?.locale(locale).clone();
        event.happenedAt = event.happenedAt?.locale(locale).clone();
      });

      this.trip.etas.forEach(eta => {
        eta.recordedAt = eta.recordedAt?.locale(locale).clone();
        eta.eta = eta.eta?.locale(locale).clone();
        eta.etaRangeStart = eta.etaRangeStart?.locale(locale).clone();
        eta.etaRangeEnd = eta.etaRangeEnd?.locale(locale).clone();
      });

      this.trip.achievedPaths.forEach(devicePath => {
        devicePath.paths.forEach(tripPath => {
          tripPath.pings.forEach(ping => ping.createdAt = ping.createdAt?.locale(locale).clone())
          tripPath.lastPing.createdAt = tripPath.lastPing.createdAt?.locale(locale).clone();
          tripPath.firstPing.createdAt = tripPath.firstPing.createdAt?.locale(locale).clone();
        });
      });
    }
  }
}

export interface EtaInfos {
  from: Moment;
  to: Moment;
  provider: string;
  lastPing: Moment;
  lastPingToNow: string;
}
